import React, { useState } from "react";
import { BiHide, BiShow } from "react-icons/bi";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import Spinner from "../../../component/page/loading/spinner";
import docAid from "../../../assets/doc-aid.png";

// Helper to get all users (default + stored)
const getAllUsers = () => {
  const defaultUsers = [
    { email: "admin@gmail.com", password: "123456", role: "admin" },
    { email: "doctor@gmail.com", password: "123456", role: "doctor" },
    { email: "patient@gmail.com", password: "123456", role: "patient" },
  ];
  const stored = JSON.parse(localStorage.getItem("users")) || [];
  return [...defaultUsers, ...stored];
};

const ForgetPassword = () => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get email from location state (passed from VerifyOTP or RegenerateOTP)
  const email = location.state?.email || "";

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      handleForgetPassword(values);
    },
  });

  const togglePassword = () => setShowPassword((prev) => !prev);

  const handleForgetPassword = (values) => {
    setLoadingSpinner(true);

    // Get all users
    let users = getAllUsers();

    // Find user by email
    const userIndex = users.findIndex((u) => u.email === email);

    if (userIndex === -1) {
      toast.error("Email not found. Please try again.");
      setLoadingSpinner(false);
      return;
    }

    // Update password (users array is a copy, but we need to update stored users only)
    // Since getAllUsers returns merged array, we need to update the stored part.
    // We'll separate stored users and update them.
    const storedUsers = JSON.parse(localStorage.getItem("users")) || [];
    const storedIndex = storedUsers.findIndex((u) => u.email === email);

    if (storedIndex !== -1) {
      storedUsers[storedIndex].password = values.newPassword;
      localStorage.setItem("users", JSON.stringify(storedUsers));
      toast.success("Password updated successfully!");
    } else {
      // If user is a default user, we can't update password (since they're hardcoded)
      // But we can allow updating by copying default user to storedUsers and then updating.
      // This will effectively override default user.
      const defaultUser = getAllUsers().find((u) => u.email === email);
      if (defaultUser) {
        // Remove from default? No, we add to stored and on next login, stored will override.
        // We'll simply add/update in storedUsers.
        const existing = storedUsers.findIndex((u) => u.email === email);
        if (existing !== -1) {
          storedUsers[existing].password = values.newPassword;
        } else {
          storedUsers.push({
            email: email,
            password: values.newPassword,
            role: defaultUser.role || "patient",
          });
        }
        localStorage.setItem("users", JSON.stringify(storedUsers));
        toast.success("Password updated successfully!");
      } else {
        toast.error("User not found.");
        setLoadingSpinner(false);
        return;
      }
    }

    setLoadingSpinner(false);
    // Navigate to login
    navigate("/");
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 text-gray-900 flex flex-col items-center justify-between">
        <div className="max-w-screen-xl m-0 sm:m-10 md:shadow sm:rounded-lg flex justify-center flex-1">
          <div className="lg:flex-1 md:bg-white text-center flex flex-col justify-center items-center">
            <div className="w-full flex justify-center items-center">
              <div className="lg:w-3/4 mb-24 md:mb-0">
                <img
                  alt="img"
                  className="w-[350px] xl:w-full lg:w-auto mb-24 md:mb-0"
                  src={docAid}
                />
              </div>
            </div>
          </div>
          {/* Single Form – responsive */}
          <div
            className="lg:w-1/2 xl:w-5/12 p-6 sm:p-12 flex items-center"
            style={{ backgroundColor: "rgb(237, 231, 242)" }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              className="w-full"
            >
              <div className="mx-auto max-w-xs">
                <p className="text-center text-gray-600 mb-4">
                  Reset password for <strong>{email || "your email"}</strong>
                </p>
                <div className="relative">
                  <input
                    className="w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="New Password"
                    autoComplete="new-password"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <button type="button" onClick={togglePassword}>
                      {showPassword ? (
                        <BiHide className="h-6 w-6" />
                      ) : (
                        <BiShow className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                </div>
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {formik.errors.newPassword}
                  </p>
                )}

                <input
                  className="w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5"
                  type="password"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Confirm New Password"
                  autoComplete="new-password"
                />
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {formik.errors.confirmPassword}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loadingSpinner}
                  className="mt-5 tracking-wide font-semibold text-white w-full py-4 rounded-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none disabled:opacity-50"
                  style={{ backgroundColor: "rgb(7, 160, 165)" }}
                >
                  {loadingSpinner ? "Updating..." : "Reset Password"}
                </button>
                <div className="text-center mt-4">
                  <span
                    className="text-sm text-blue-500 cursor-pointer hover:underline"
                    onClick={() => navigate("/")}
                  >
                    Back to Login
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
      {loadingSpinner && <Spinner />}
    </>
  );
};

export default ForgetPassword;